// Package Imports
import { queryOptions, useMutation } from '@tanstack/react-query'

// Core Imports
import type { TResponseError } from '@/core/types/axios'
import type { TMutation } from '@/core/types/react-query'

// Module Imports
import {
  PERIOD_LIST_KEY,
  PERIOD_OPTIONS_KEY,
} from '@/modules/period/period.constants'
import {
  activatePeriod,
  createPeriod,
  deletePeriod,
  getPeriodDetail,
  getPeriodList,
  getPeriodOptions,
  passivePeriod,
  updatePeriod,
} from '@/modules/period/period.service'
import type {
  TCreatePeriodRequest,
  TPeriod,
  TUpdatePeriodRequest,
} from '@/modules/period/period.types'

export const periodListQueryOptions = (
  page: number,
  query: string,
  status?: string
) =>
  queryOptions({
    queryKey: [...PERIOD_LIST_KEY, page, query, status ?? 'ALL'],
    queryFn: () => getPeriodList(page, query, status),
  })

export const periodDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['periodDetail', id],
    queryFn: () => getPeriodDetail(id),
    staleTime: 0,
  })

export const periodOptionsQueryOptions = () =>
  queryOptions({
    queryKey: PERIOD_OPTIONS_KEY,
    queryFn: getPeriodOptions,
    staleTime: 0,
  })

export function useCreatePeriod({
  onSuccess,
  onError,
}: TMutation<TPeriod, TResponseError, TCreatePeriodRequest>) {
  return useMutation<TPeriod, TResponseError, TCreatePeriodRequest>({
    mutationFn: createPeriod,
    onSuccess,
    onError,
  })
}

export function useUpdatePeriod({
  onSuccess,
  onError,
}: TMutation<
  TPeriod,
  TResponseError,
  { id: string; body: TUpdatePeriodRequest }
>) {
  return useMutation<
    TPeriod,
    TResponseError,
    { id: string; body: TUpdatePeriodRequest }
  >({
    mutationFn: ({ id, body }) => updatePeriod(id, body),
    onSuccess,
    onError,
  })
}

export function useActivatePeriod({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: activatePeriod,
    onSuccess,
    onError,
  })
}

export function usePassivePeriod({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: passivePeriod,
    onSuccess,
    onError,
  })
}

export function useDeletePeriod({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: deletePeriod,
    onSuccess,
    onError,
  })
}
